const isFiniteNumber = (value) =>
  typeof value === "number" && Number.isFinite(value);

const roundToTwo = (value) => Math.round(value * 100) / 100;

const finiteNumber = (value) => {
  if (isFiniteNumber(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const valueOrNull = (value) => (value === undefined ? null : value);

const lineId = (line) => line && (line.id ?? line.upsell_id ?? null);

const lineName = (line, isSynthetic) => {
  if (line && line.name != null) {
    return line.name;
  }
  return isSynthetic ? "No line item recorded" : "Unnamed item";
};

const normalizedValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  return String(value);
};

const normalizedName = (value) => {
  const normalized = normalizedValue(value);
  if (normalized === null) {
    return null;
  }
  const trimmed = normalized.trim();
  return trimmed === "" ? null : trimmed;
};

const quantityFor = (line, isSynthetic) =>
  isSynthetic ? null : line.quantity == null ? 1 : line.quantity;

const amountFor = (line, quantity) => {
  if (!line) {
    return null;
  }
  if (line.amount != null) {
    const amount = finiteNumber(line.amount);
    return amount === null ? valueOrNull(line.amount) : amount;
  }
  const price = finiteNumber(line.price);
  const numericQuantity = finiteNumber(quantity);
  if (price !== null && numericQuantity !== null) {
    return roundToTwo(price * numericQuantity);
  }
  return null;
};

const hasRecordedFee = (value) =>
  value !== null &&
  value !== undefined &&
  (typeof value !== "string" || value.trim() !== "");

const feeValueFor = (value) => (hasRecordedFee(value) ? value : null);

const declaresGovernmentFee = (line) =>
  hasRecordedFee(line.government_fee) || hasRecordedFee(line.govt_fee);

// A line without a government fee field records no fee data, so the table can
// show N/A instead of a zero that reads as a recorded fee.
const governmentFeeFor = (line) => {
  if (!line) {
    return null;
  }
  const aliases = [line.government_fee, line.govt_fee]
    .map(finiteNumber)
    .filter((value) => value !== null && value > 0);
  if (aliases.length) {
    const quantity = finiteNumber(line.quantity) ?? 1;
    return roundToTwo(Math.max(...aliases) * quantity);
  }
  if (line.type === "govt_fee" || line.type === "government_fee") {
    const typedAmount = finiteNumber(
      line.amount != null ? line.amount : line.price,
    );
    if (typedAmount !== null) {
      return typedAmount;
    }
  }
  return declaresGovernmentFee(line) ? 0 : null;
};

const transactionFor = (line, purchase) =>
  line &&
  (line.transaction_id ??
    line.orderId ??
    line.order_id ??
    line.transactionId ??
    (purchase && purchase.transaction_id != null
      ? purchase.transaction_id
      : null));

const historyTimestampFor = (transactionId, paymentInfoHistory) => {
  if (transactionId == null) {
    return null;
  }
  const transaction = String(transactionId);
  const payment = paymentInfoHistory.find(
    (entry) =>
      entry && entry.orderId != null && String(entry.orderId) === transaction,
  );
  return payment && payment.createdAt != null ? payment.createdAt : null;
};

const reconciliationFor = (purchase, source) => {
  if (source === "legacy-cart-products" || !purchase) {
    return null;
  }
  const reconciliation = purchase.reconciliation;
  if (!reconciliation || typeof reconciliation !== "object") {
    return null;
  }
  const status =
    reconciliation.status || reconciliation.result || reconciliation.state;
  const matched =
    reconciliation.matched === true ||
    (typeof status === "string" && status.toLowerCase() === "matched");
  if (matched) {
    return "Matched";
  }
  const mismatched =
    reconciliation.matched === false ||
    (typeof status === "string" &&
      ["mismatch", "mismatched"].includes(status.toLowerCase()));
  const difference =
    reconciliation.difference ??
    reconciliation.amount_difference ??
    reconciliation.amountDifference ??
    reconciliation.delta;
  if (mismatched && isFiniteNumber(difference)) {
    return `Mismatch (${roundToTwo(difference)})`;
  }
  return null;
};

const hasReconciliationMismatch = (purchase) => {
  const reconciliation = purchase && purchase.reconciliation;
  if (!reconciliation || typeof reconciliation !== "object") {
    return false;
  }
  const status =
    reconciliation.status || reconciliation.result || reconciliation.state;
  return (
    reconciliation.matched === false ||
    (typeof status === "string" &&
      ["mismatch", "mismatched"].includes(status.toLowerCase()))
  );
};

const chargedAmountFor = (purchase, isLegacy) => {
  if (!purchase) {
    return null;
  }
  const amount = finiteNumber(purchase.charged_amount);
  if (amount === null) {
    return null;
  }
  if (isLegacy) {
    return amount > 0 && !hasReconciliationMismatch(purchase) ? amount : null;
  }
  return amount;
};

const keyFor = (purchaseIndex, lineIndex, source, transactionId, itemId) =>
  [purchaseIndex, lineIndex, source, transactionId, itemId]
    .map((value) => normalizedValue(value) || "")
    .join(":");

const rowFor = ({
  line,
  purchase,
  purchaseIndex,
  lineIndex,
  source,
  isSynthetic,
  paymentInfoHistory,
}) => {
  const actualLine = line || {};
  const id = lineId(actualLine);
  const quantity = quantityFor(actualLine, isSynthetic);
  const transactionId = transactionFor(actualLine, purchase);
  const rawSource = source || (purchase && purchase.source) || "purchase";
  const isLegacy = rawSource === "legacy-cart-products";
  const itemAmount = amountFor(actualLine, quantity);
  const chargedAmount = chargedAmountFor(purchase, isLegacy);

  return {
    key: keyFor(purchaseIndex, lineIndex, rawSource, transactionId, id),
    source: isLegacy
      ? "Legacy cart"
      : rawSource === "Applicant cart"
      ? "Applicant cart"
      : rawSource === "UCC"
      ? "UCC"
      : "Purchase",
    id,
    name: lineName(line, isSynthetic),
    itemAmount,
    governmentFee: governmentFeeFor(line),
    shippingFee: feeValueFor(line && line.shipping_fee),
    quantity,
    serviceSpeed:
      line && line.service_speed != null
        ? line.service_speed
        : line && line.speed != null
        ? line.speed
        : null,
    transactionId,
    currency:
      line && line.currency != null
        ? line.currency
        : purchase && purchase.currency != null
        ? purchase.currency
        : null,
    chargedAmount,
    purchasedAt:
      historyTimestampFor(transactionId, paymentInfoHistory) ??
      actualLine.createdAt ??
      null,
    reconciliation: reconciliationFor(purchase, rawSource),
  };
};

const identityId = (line) => normalizedValue(lineId(line));

// Product identity compares IDs when both exist, otherwise normalized names; missing values never match.
export const productDiscriminatorMatches = (left, right) => {
  const leftId = identityId(left);
  const rightId = identityId(right);
  if (leftId && rightId) {
    return leftId === rightId;
  }
  const leftName = normalizedName(left && left.name);
  const rightName = normalizedName(right && right.name);
  return Boolean(leftName && rightName && leftName === rightName);
};

const lineTransaction = (line) =>
  line &&
  (line.transaction_id ?? line.orderId ?? line.order_id ?? line.transactionId);

// A cart line is usable only when it carries a non-empty product or amount/fee field.
const hasUsableCartLine = (line) => {
  if (!line || typeof line !== "object") {
    return false;
  }
  return Boolean(
    normalizedName(lineId(line)) ||
      normalizedName(line.name) ||
      [line.amount, line.price, line.government_fee, line.govt_fee].some(
        (value) =>
          value !== null &&
          value !== undefined &&
          (typeof value !== "string" || value.trim() !== ""),
      ),
  );
};

const hasStrongCartMatch = (cartLine, snapshotLines) => {
  const cartTransaction = normalizedValue(lineTransaction(cartLine));
  if (!cartTransaction) {
    return false;
  }
  return snapshotLines.some((snapshotLine) => {
    if (
      normalizedValue(
        transactionFor(snapshotLine.line, snapshotLine.purchase),
      ) !== cartTransaction
    ) {
      return false;
    }
    return productDiscriminatorMatches(cartLine, snapshotLine.line);
  });
};

// Synthetic rows inherit recorded parent product, amount, fee, and timestamp fields.
const syntheticLineFor = (purchase) => {
  const product =
    purchase.product && typeof purchase.product === "object"
      ? purchase.product
      : {};
  const productId =
    typeof purchase.product === "number" ? purchase.product : null;
  const productName =
    typeof purchase.product === "string" ? purchase.product : null;

  return {
    id:
      purchase.productId ??
      purchase.product_id ??
      product.id ??
      productId ??
      null,
    name:
      purchase.productName ??
      purchase.product_name ??
      product.name ??
      productName ??
      purchase.name ??
      null,
    amount:
      purchase.amount ??
      purchase.price ??
      product.amount ??
      product.price ??
      null,
    government_fee:
      feeValueFor(purchase.government_fee) ??
      feeValueFor(product.government_fee),
    govt_fee: feeValueFor(purchase.govt_fee) ?? feeValueFor(product.govt_fee),
    shipping_fee:
      feeValueFor(purchase.shipping_fee) ?? feeValueFor(product.shipping_fee),
    createdAt: purchase.createdAt ?? purchase.created_at ?? null,
    currency: purchase.currency ?? product.currency ?? null,
    service_speed:
      purchase.service_speed ?? product.service_speed ?? product.speed ?? null,
  };
};

const purchaseLinesFor = (purchase) => {
  const rawLines = Array.isArray(purchase.cart_products)
    ? purchase.cart_products
    : [];
  const linesWithData = rawLines.filter(hasUsableCartLine);
  return linesWithData.length > 0
    ? { lines: linesWithData, isSynthetic: false }
    : { lines: [syntheticLineFor(purchase)], isSynthetic: true };
};

const productIdentityForLine = (line) => ({
  id: lineId(line),
  name: normalizedName(line && line.name),
});

const productIdentitiesForLines = (lines) =>
  lines
    .filter(
      (line) =>
        normalizedValue(lineId(line)) !== null ||
        normalizedName(line && line.name) !== null,
    )
    .map(productIdentityForLine);

export const getCartProductIdentities = (cartProducts = []) =>
  productIdentitiesForLines(Array.isArray(cartProducts) ? cartProducts : []);

export const getPurchaseProductIdentities = (purchases = []) => {
  const safePurchases = Array.isArray(purchases) ? purchases : [];
  return safePurchases.reduce((identities, purchase) => {
    if (!purchase || typeof purchase !== "object") {
      return identities;
    }
    const { lines } = purchaseLinesFor(purchase);
    return identities.concat(productIdentitiesForLines(lines));
  }, []);
};

// Applicant cart lines inherit recorded applicant metadata only when their fields are absent.
const inheritedCartLineFor = (line, cartMetadata) => {
  const safeLine = line && typeof line === "object" ? line : {};
  const safeMetadata =
    cartMetadata && typeof cartMetadata === "object" ? cartMetadata : {};
  return {
    ...safeMetadata,
    ...safeLine,
    orderId:
      safeLine.orderId ??
      safeLine.order_id ??
      safeLine.transaction_id ??
      safeLine.transactionId ??
      safeMetadata.orderId,
    createdAt:
      safeLine.createdAt ?? safeLine.created_at ?? safeMetadata.createdAt,
    government_fee:
      feeValueFor(safeLine.government_fee) ??
      feeValueFor(safeMetadata.government_fee),
    govt_fee:
      feeValueFor(safeLine.govt_fee) ?? feeValueFor(safeMetadata.govt_fee),
    shipping_fee:
      feeValueFor(safeLine.shipping_fee) ??
      feeValueFor(safeMetadata.shipping_fee),
  };
};

const existingRowsMatchPayment = (payment, existingLines) => {
  const paymentOrder = normalizedValue(payment.orderId);
  if (!paymentOrder) {
    return false;
  }
  const paymentLine = {
    id: payment.productId ?? null,
    name: payment.productName ?? null,
  };
  return existingLines.some(({ line, purchase }) => {
    const existingTransaction = normalizedValue(transactionFor(line, purchase));
    return (
      existingTransaction === paymentOrder &&
      productDiscriminatorMatches(paymentLine, line)
    );
  });
};

export function buildAlreadyChargedRows({
  purchases = [],
  paymentInfoHistory = [],
  cartProducts = [],
  cartMetadata = {},
} = {}) {
  // Line identity requires an exact non-null transaction and product discriminator; missing discriminators never match.
  const safePurchases = Array.isArray(purchases) ? purchases : [];
  const safeHistory = Array.isArray(paymentInfoHistory)
    ? paymentInfoHistory
    : [];
  const safeCartProducts = Array.isArray(cartProducts) ? cartProducts : [];
  const rows = [];
  const existingLines = [];
  const snapshotLines = [];

  safePurchases.forEach((purchase, purchaseIndex) => {
    if (!purchase || typeof purchase !== "object") {
      return;
    }
    const { lines, isSynthetic } = purchaseLinesFor(purchase);
    lines.forEach((line, lineIndex) => {
      const safeLine = line && typeof line === "object" ? line : {};
      rows.push(
        rowFor({
          line: safeLine,
          purchase,
          purchaseIndex,
          lineIndex,
          source: purchase.source,
          isSynthetic,
          paymentInfoHistory: safeHistory,
        }),
      );
      const lineRecord = { line: safeLine, purchase };
      existingLines.push(lineRecord);
      snapshotLines.push(lineRecord);
    });
  });

  safeCartProducts.forEach((product, cartIndex) => {
    const cartLine = inheritedCartLineFor(product, cartMetadata);
    if (
      !product ||
      typeof product !== "object" ||
      hasStrongCartMatch(cartLine, snapshotLines)
    ) {
      return;
    }
    rows.push(
      rowFor({
        line: cartLine,
        purchase: null,
        purchaseIndex: safePurchases.length,
        lineIndex: cartIndex,
        source: "Applicant cart",
        isSynthetic: false,
        paymentInfoHistory: safeHistory,
      }),
    );
    existingLines.push({ line: cartLine, purchase: null });
  });

  safeHistory.forEach((payment, paymentIndex) => {
    if (
      !payment ||
      typeof payment !== "object" ||
      existingRowsMatchPayment(payment, existingLines)
    ) {
      return;
    }
    const paymentLine = {
      id: payment.productId ?? null,
      name: payment.productName ?? null,
      amount: payment.amount ?? null,
      quantity: 1,
      transaction_id: payment.orderId ?? null,
      currency: payment.currency ?? null,
      createdAt: payment.createdAt ?? null,
      government_fee: feeValueFor(payment.government_fee),
      govt_fee: feeValueFor(payment.govt_fee),
      shipping_fee: feeValueFor(payment.shipping_fee),
    };
    rows.push(
      rowFor({
        line: paymentLine,
        purchase: null,
        purchaseIndex: safePurchases.length + safeCartProducts.length,
        lineIndex: paymentIndex,
        source: "UCC",
        isSynthetic: false,
        paymentInfoHistory: safeHistory,
      }),
    );
    existingLines.push({ line: paymentLine, purchase: null });
  });

  return rows;
}
